import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, TextField, InputAdornment, MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Layout from '../components/Layout';
import { scopeService, proposalService, contractService } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

export default function ProposalReview() {
  const { user } = useAuth();
  const [scopes, setScopes] = useState([]);
  const [selectedScope, setSelectedScope] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [search, setSearch] = useState('');
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, proposal: null });
  const [contractDialog, setContractDialog] = useState({ open: false, proposal: null });
  const [viewDialog, setViewDialog] = useState({ open: false, proposal: null });
  const [contractForm, setContractForm] = useState({ start_date: '', end_date: '' });
  const [loading, setLoading] = useState(false);
  const [invitedSubs, setInvitedSubs] = useState([]);
  const [pipelineFilter, setPipelineFilter] = useState('open');
  const [confirmAction, setConfirmAction] = useState({ open: false, type: '', scope: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({ project_id: '', title: '', description: '', trade_category: '', budget_estimate: '', deadline: '' });
  const [projects, setProjects] = useState([]);
  const [tradeCategories, setTradeCategories] = useState([]);
  const [inviteDialog, setInviteDialog] = useState({ open: false, scope: null });
  const [subcontractors, setSubcontractors] = useState([]);
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [inviteAlert, setInviteAlert] = useState('');

  const location = useLocation();

  useEffect(() => {
    fetchScopes();
    fetchProjectsAndTrades();
  }, []);

  // Deep-link: if URL has ?scope_id=..., auto-select that scope once scopes are loaded
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get('scope_id');
    if (!sid || !scopes.length) return;
    const match = scopes.find(s => s.id === sid);
    if (match && (!selectedScope || selectedScope.id !== sid)) {
      setPipelineFilter('all');
      fetchProposals(match);
    }
  }, [scopes, location.search]);

  const fetchProjectsAndTrades = async () => {
    try {
      const [projRes, tradeRes] = await Promise.all([
        api.get('/projects'),
        fetch('https://construction-backend-531984037368.us-central1.run.app/api/settings/trades')
      ]);
      setProjects(projRes.data || []);
      const trades = await tradeRes.json();
      setTradeCategories(Array.isArray(trades) ? trades : []);
    } catch (err) {
      setProjects([]);
      setTradeCategories([]);
    }
  };

  const fetchScopes = async () => {
    try {
      const projectsRes = await scopeService.getOpen();
      // Also get awarded scopes
      const res = await fetch(`https://construction-backend-531984037368.us-central1.run.app/api/scopes/my`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const myScopes = res.ok ? await res.json() : [];
      setScopes([...projectsRes.data, ...myScopes].filter((s, i, arr) => arr.findIndex(x => x.id === s.id) === i));
    } catch (err) {
      // fallback to open scopes only
      try {
        const res = await scopeService.getOpen();
        setScopes(res.data);
      } catch (e) {}
    }
  };

  const fetchProposals = async (scope) => {
    setSelectedScope(scope);
    try {
      const res = await proposalService.getByScope(scope.id);
      setProposals(res.data);
    } catch (err) {
      setProposals([]);
    }
    try {
      const invRes = await api.get('/scopes/' + scope.id + '/invitations');
      setInvitedSubs(invRes.data || []);
    } catch (err) {
      setInvitedSubs([]);
    }
  };

  const handleSelectProposal = async () => {
    const proposal = confirmDialog.proposal;
    // Just open contract dialog - don't mark accepted yet
    setConfirmDialog({ open: false, proposal: null });
    setContractDialog({ open: true, proposal });
  };

  const handleCreateContract = async () => {
    const proposal = contractDialog.proposal;
    setLoading(true);
    try {
      // First select the proposal
      await proposalService.select(proposal.id);
      // Then create the contract
      await contractService.create({
        proposal_id: proposal.id,
        project_id: selectedScope?.project_id,
        scope_id: selectedScope?.id,
        subcontractor_id: proposal.subcontractor_id,
        contract_amount: proposal.proposed_amount,
        start_date: contractForm.start_date,
        end_date: contractForm.end_date,
      });
      setProposals(proposals.map(p => ({
        ...p,
        status: p.id === proposal.id ? 'accepted' : p.status
      })));
      setContractDialog({ open: false, proposal: null });
      showAlert('Contract created successfully! Go to Contracts to sign it.', 'success');
    } catch (err) {
      showAlert('Failed to create contract: ' + (err.response?.data?.error || err.message), 'error');
    }
    setLoading(false);
  };

  const handleWithdraw = async (scope) => {
    setActionLoading(true);
    try {
      await api.put('/scopes/' + scope.id + '/withdraw');
      setScopes(prev => prev.map(sc => sc.id === scope.id ? { ...sc, status: 'withdrawn' } : sc));
      if (selectedScope?.id === scope.id) setSelectedScope(prev => ({ ...prev, status: 'withdrawn' }));
      showAlert('Scope withdrawn. All invited subs notified.', 'success');
    } catch(e) { showAlert(e.response?.data?.error || 'Failed to withdraw', 'error'); }
    setActionLoading(false);
    setConfirmAction({ open: false, type: '', scope: null });
  };

  const handleDelete = async (scope) => {
    setActionLoading(true);
    try {
      await api.delete('/scopes/' + scope.id);
      setScopes(prev => prev.filter(sc => sc.id !== scope.id));
      if (selectedScope?.id === scope.id) { setSelectedScope(null); setProposals([]); }
      showAlert('Scope deleted.', 'success');
    } catch(e) { showAlert(e.response?.data?.error || 'Failed to delete', 'error'); }
    setActionLoading(false);
    setConfirmAction({ open: false, type: '', scope: null });
  };

  const showAlert = (message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 4000);
  };

  const statusConfig = {
    submitted: { color: '#fff3e0', text: '#e65100', label: 'Submitted' },
    under_review: { color: '#e3f2fd', text: '#1565c0', label: 'Under Review' },
    accepted: { color: '#e8f5e9', text: '#2e7d32', label: 'Accepted ✓' },
    rejected: { color: '#ffebee', text: '#c62828', label: 'Rejected' },
  };

  const pipelineConfig = {
    all:        { label: 'All',        color: '#1a237e', bg: '#e8eaf6' },
    open:       { label: 'Open',       color: '#2e7d32', bg: '#e8f5e9' },
    bidding:    { label: 'Bidding',    color: '#e65100', bg: '#fff3e0' },
    awarded:    { label: 'Awarded',    color: '#1565c0', bg: '#e3f2fd' },
    contracted: { label: 'Contracted', color: '#6a1b9a', bg: '#f3e5f5' },
    withdrawn:  { label: 'Withdrawn',  color: '#757575', bg: '#f5f5f5' },
  };
  const pipelineCounts = {
    all: scopes.length,
    open: scopes.filter(sc => sc.status === 'open' && Number(sc.proposal_count||0) === 0).length,
    bidding: scopes.filter(sc => sc.status === 'open' && Number(sc.proposal_count||0) > 0).length,
    awarded: scopes.filter(sc => sc.status === 'awarded').length,
    contracted: scopes.filter(sc => sc.status === 'contracted').length,
    withdrawn: scopes.filter(sc => sc.status === 'withdrawn').length,
  };
  const filteredScopes = scopes.filter(s =>
    (pipelineFilter === 'all' ||
     (pipelineFilter === 'open' && s.status === 'open' && Number(s.proposal_count||0) === 0) ||
     (pipelineFilter === 'bidding' && s.status === 'open' && Number(s.proposal_count||0) > 0) ||
     (pipelineFilter === 'awarded' && s.status === 'awarded') ||
     (pipelineFilter === 'contracted' && s.status === 'contracted') ||
     (pipelineFilter === 'withdrawn' && s.status === 'withdrawn')) &&
    (s.title?.toLowerCase().includes(search.toLowerCase()) ||
     s.trade_category?.toLowerCase().includes(search.toLowerCase()))
  );


  const fetchSubcontractors = async () => {
    try {
      const res = await api.get('/vendors?role=subcontractor');
      setSubcontractors(res.data || []);
    } catch (err) {
      setSubcontractors([]);
    }
  };

  const handleInvite = async () => {
    if (!selectedSubs.length) return;
    try {
      await api.post(`/scopes/${inviteDialog.scope.id}/invite`, { subcontractor_ids: selectedSubs });
      setInviteAlert('Invitations sent successfully!');
      setTimeout(() => { setInviteDialog({ open: false, scope: null }); setSelectedSubs([]); setInviteAlert(''); }, 1500);
      if (selectedScope?.id === inviteDialog.scope.id) {
        fetchProposals(selectedScope);
      }
    } catch (err) {
      setInviteAlert('Failed to send invitations');
    }
  };

  const handleCreateScope = async () => {
    if (!createForm.project_id || !createForm.title || !createForm.description) {
      showAlert('Please fill in all required fields', 'error');
      return;
    }
    setLoading(true);
    try {
      await api.post('/scopes', createForm);
      showAlert('Scope created successfully', 'success');
      setCreateDialog(false);
      setCreateForm({ project_id: '', title: '', description: '', trade_category: '', budget_estimate: '', deadline: '' });
      fetchScopes();
    } catch (err) {
      showAlert('Failed to create scope', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 3, borderRadius: 2 }}>{alert.message}</Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e' }}>Scopes & Proposals</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>Manage scopes, review proposals, and award contracts</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialog(true)}
          sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d47a1' }, borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>
          New Scope
        </Button>
      </Box>
      {/* Pipeline filter bar */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {Object.entries(pipelineConfig).map(([key, cfg]) => (
          <Box key={key} onClick={() => setPipelineFilter(key)}
            sx={{ cursor: 'pointer', px: 2, py: 0.75, borderRadius: 2, fontSize: 12, fontWeight: 700,
              bgcolor: pipelineFilter === key ? cfg.color : cfg.bg,
              color: pipelineFilter === key ? 'white' : cfg.color,
              border: '1px solid ' + (pipelineFilter === key ? cfg.color : 'transparent'),
              display: 'flex', alignItems: 'center', gap: 0.75, transition: 'all 0.15s' }}>
            {cfg.label}
            <Box sx={{ bgcolor: pipelineFilter === key ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)',
              borderRadius: 10, px: 0.75, fontSize: 11, fontWeight: 800, minWidth: 18, textAlign: 'center' }}>
              {pipelineCounts[key]}
            </Box>
          </Box>
        ))}
      </Box>

      <Grid container spacing={3}>
        {/* Left: Scopes list */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e8eaf6' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 1 }}>Your Scopes</Typography>
              <TextField fullWidth placeholder="Search scopes..." value={search}
                onChange={e => setSearch(e.target.value)} size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999', fontSize: 18 }} /></InputAdornment> }} />
            </Box>
            <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
              {filteredScopes.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <AssignmentIcon sx={{ fontSize: 40, color: '#e0e0e0' }} />
                  <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>No scopes found</Typography>
                </Box>
              )}
              {filteredScopes.map(scope => (
                <Box key={scope.id}
                  onClick={() => fetchProposals(scope)}
                  sx={{
                    p: 2, cursor: 'pointer', borderBottom: '1px solid #f5f6fa',
                    bgcolor: selectedScope?.id === scope.id ? '#e8eaf6' : 'white',
                    '&:hover': { bgcolor: '#f5f6fa' }
                  }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#1a237e', lineHeight: 1.2 }} noWrap>
                        {scope.address ? [scope.address, scope.city, scope.state].filter(Boolean).join(', ') : scope.project_name || scope.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888' }} noWrap>{scope.title}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25, flexShrink: 0 }}>
                      <Chip label={scope.status === 'open' && Number(scope.proposal_count||0) > 0 ? 'Bidding' : scope.status}
                        size="small" sx={{ fontSize: 10, height: 18,
                          bgcolor: scope.status === 'contracted' ? '#f3e5f5' : scope.status === 'awarded' ? '#e3f2fd' : scope.status === 'withdrawn' ? '#f5f5f5' : Number(scope.proposal_count||0) > 0 ? '#fff3e0' : '#e8f5e9',
                          color: scope.status === 'contracted' ? '#6a1b9a' : scope.status === 'awarded' ? '#1565c0' : scope.status === 'withdrawn' ? '#757575' : Number(scope.proposal_count||0) > 0 ? '#e65100' : '#2e7d32' }} />
                      {Number(scope.proposal_count||0) > 0 && (
                        <Typography variant="caption" sx={{ fontSize: 10, color: '#e65100', fontWeight: 700 }}>
                          {scope.proposal_count} proposal{scope.proposal_count > 1 ? 's' : ''}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#999' }}>{scope.trade_category}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <AttachMoneyIcon sx={{ fontSize: 14, color: '#43a047' }} />
                    <Typography variant="caption" sx={{ color: '#43a047', fontWeight: 600 }}>
                      ${Number(scope.budget_estimate).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>

        {/* Right: Proposals */}
        <Grid item xs={12} md={8}>
          {!selectedScope ? (
            <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ textAlign: 'center' }}>
                <AssignmentIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#999' }}>Select a scope to view proposals</Typography>
              </Box>
            </Card>
          ) : (
            <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3 }}>
              <Box sx={{ p: 3, borderBottom: '1px solid #e8eaf6' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1a237e' }}>
                  {selectedScope.address ? [selectedScope.address, selectedScope.city, selectedScope.state].filter(Boolean).join(', ') : selectedScope.project_name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>{selectedScope.title}</Typography>
                <Typography variant="caption" sx={{ color: '#999' }}>{proposals.length} proposal(s) received</Typography>
                {selectedScope.status !== 'contracted' && selectedScope.status !== 'withdrawn' && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                    {selectedScope.status !== 'awarded' && (
                      <Button size="small" variant="outlined"
                        onClick={(e) => { e.stopPropagation(); fetchSubcontractors(); setInviteDialog({ open: true, scope: selectedScope }); }}
                        sx={{ borderRadius: 2, fontSize: 11 }}>+ Invite Subs</Button>
                    )}
                    {selectedScope.status !== 'awarded' && (
                      <Button size="small" variant="outlined" color="warning"
                        onClick={() => setConfirmAction({ open: true, type: 'withdraw', scope: selectedScope })}
                        sx={{ borderRadius: 2, fontSize: 11, borderColor: '#e65100', color: '#e65100' }}>Withdraw</Button>
                    )}
                    <Button size="small" variant="outlined" color="error"
                      onClick={() => setConfirmAction({ open: true, type: 'delete', scope: selectedScope })}
                      sx={{ borderRadius: 2, fontSize: 11 }}>Delete</Button>
                  </Box>
                )}
              </Box>
              {/* Invited subs */}
              {invitedSubs.length > 0 && (
                <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0', bgcolor: '#fafafa' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Invited Subcontractors ({invitedSubs.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {invitedSubs.map(inv => {
                      const proposal = proposals.find(p => p.subcontractor_id === inv.subcontractor_id);
                      const subStatus = proposal ? proposal.status : (inv.status === 'declined' ? 'declined' : 'invited');
                      const statusColors = { submitted: ['#fff3e0','#e65100'], accepted: ['#e8f5e9','#2e7d32'], rejected: ['#ffebee','#c62828'], declined: ['#f3e5f5','#7b1fa2'], invited: ['#f5f5f5','#757575'] };
                      const [bg, color] = statusColors[subStatus] || statusColors.invited;
                      return (
                        <Box key={inv.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: '4px 10px', borderRadius: 2, bgcolor: bg, border: '1px solid ' + color + '33' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color }}>{inv.first_name || inv.email || 'Sub'}</Typography>
                          <Chip label={subStatus} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: color + '22', color }} />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
              {proposals.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <PersonIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                  <Typography sx={{ color: '#999' }}>No proposals yet for this scope</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead sx={{ bgcolor: '#f5f6fa' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Contractor</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Proposal</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Timeline</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {proposals.map(proposal => (
                        <TableRow key={proposal.id} sx={{
                          '&:hover': { bgcolor: '#f5f6fa' },
                          bgcolor: proposal.status === 'accepted' ? '#f1f8e9' : 'white'
                        }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ bgcolor: '#1a237e', width: 32, height: 32, fontSize: 14 }}>
                                {proposal.first_name?.[0]}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {proposal.first_name} {proposal.last_name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#999' }}>{proposal.email}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{proposal.title}</Typography>
                            {proposal.notes && <Typography variant="caption" sx={{ color: '#666' }}>{proposal.notes}</Typography>}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e' }}>
                              ${Number(proposal.proposed_amount).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {proposal.estimated_start_date && (
                              <Box>
                                <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                  {new Date(proposal.estimated_start_date).toLocaleDateString()}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#666' }}>
                                  — {new Date(proposal.estimated_end_date).toLocaleDateString()}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={statusConfig[proposal.status]?.label} size="small"
                              sx={{ bgcolor: statusConfig[proposal.status]?.color, color: statusConfig[proposal.status]?.text, fontWeight: 600 }} />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Button size="small" variant="outlined"
                                onClick={(e) => { e.stopPropagation(); setViewDialog({ open: true, proposal }); }}
                                sx={{ borderRadius: 1, fontSize: 11 }}>
                                View
                              </Button>
                              {proposal.status === 'submitted' && (
                                <Button size="small" variant="contained" startIcon={<CheckCircleIcon />}
                                  onClick={() => setConfirmDialog({ open: true, proposal })}
                                  sx={{ bgcolor: '#2e7d32', borderRadius: 1, fontSize: 11 }}>
                                  Select
                                </Button>
                              )}
                              {proposal.status === 'accepted' && (
                                <Chip label="Winner" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Confirm Select Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, proposal: null })}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Select Winning Proposal</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to select <strong>{confirmDialog.proposal?.title}</strong> by <strong>{confirmDialog.proposal?.first_name} {confirmDialog.proposal?.last_name}</strong>?</Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f6fa', borderRadius: 2 }}>
            <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 700 }}>
              ${Number(confirmDialog.proposal?.proposed_amount).toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: '#999' }}>Contract Amount</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#666', mt: 2 }}>All other proposals will be automatically rejected.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setConfirmDialog({ open: false, proposal: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSelectProposal} disabled={loading}
            sx={{ bgcolor: '#2e7d32', borderRadius: 2, px: 3 }}>
            {loading ? 'Processing...' : 'Confirm Selection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Contract Dialog */}
      <Dialog open={contractDialog.open} onClose={() => setContractDialog({ open: false, proposal: null })}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Create Contract</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Set the contract dates for <strong>{contractDialog.proposal?.first_name} {contractDialog.proposal?.last_name}</strong>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth label="Start Date" type="date" value={contractForm.start_date}
                onChange={e => setContractForm({...contractForm, start_date: e.target.value})}
                margin="normal" InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="End Date" type="date" value={contractForm.end_date}
                onChange={e => setContractForm({...contractForm, end_date: e.target.value})}
                margin="normal" InputLabelProps={{ shrink: true }} required />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setContractDialog({ open: false, proposal: null })}>Skip for Now</Button>
          <Button variant="contained" onClick={handleCreateContract} disabled={loading}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            {loading ? 'Creating...' : 'Create Contract'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Proposal Dialog */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, proposal: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>
          Proposal — {viewDialog.proposal?.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar sx={{ bgcolor: '#1a237e' }}>{viewDialog.proposal?.first_name?.[0]}</Avatar>
            <Box>
              <Typography fontWeight={600}>{viewDialog.proposal?.first_name} {viewDialog.proposal?.last_name}</Typography>
              <Typography variant="caption" color="text.secondary">{viewDialog.proposal?.email}</Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2, bgcolor: '#f5f6fa', borderRadius: 2, mb: 2 }}>
            <Typography variant="h5" color="primary" fontWeight={700}>${Number(viewDialog.proposal?.proposed_amount || 0).toLocaleString()}</Typography>
            <Typography variant="caption" color="text.secondary">Proposed Amount</Typography>
          </Box>
          {viewDialog.proposal?.estimated_start_date && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>Timeline</Typography>
              <Typography variant="body2">{new Date(viewDialog.proposal.estimated_start_date).toLocaleDateString()} — {new Date(viewDialog.proposal.estimated_end_date).toLocaleDateString()}</Typography>
            </Box>
          )}
          {viewDialog.proposal?.notes && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>Notes</Typography>
              <Typography variant="body2">{viewDialog.proposal.notes}</Typography>
            </Box>
          )}
          {viewDialog.proposal?.attachments && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Attachments</Typography>
              {(Array.isArray(viewDialog.proposal.attachments) ? viewDialog.proposal.attachments : JSON.parse(viewDialog.proposal.attachments || '[]')).map((att, i) => (
                <Box key={i} sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2">{att.name || att.originalName || `Attachment ${i+1}`}</Typography>
                  <Button size="small" variant="outlined" href={att.url || att.gcsUrl} target="_blank">View File</Button>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog({ open: false, proposal: null })}>Close</Button>
          {viewDialog.proposal?.status === 'submitted' && (
            <Button variant="contained" sx={{ bgcolor: '#2e7d32' }}
              onClick={() => { setViewDialog({ open: false, proposal: null }); setConfirmDialog({ open: true, proposal: viewDialog.proposal }); }}>
              Select This Proposal
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* Confirm withdraw/delete dialog */}
      <Dialog open={confirmAction.open} onClose={() => setConfirmAction({ open: false, type: '', scope: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: confirmAction.type === 'delete' ? '#c62828' : '#e65100' }}>
          {confirmAction.type === 'delete' ? 'Delete Scope?' : 'Withdraw Scope?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirmAction.type === 'delete'
              ? 'This will permanently delete the scope. Cannot be undone.'
              : 'This will withdraw the scope and notify all invited subcontractors.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmAction({ open: false, type: '', scope: null })}>Cancel</Button>
          <Button variant="contained" disabled={actionLoading}
            onClick={() => confirmAction.type === 'delete' ? handleDelete(confirmAction.scope) : handleWithdraw(confirmAction.scope)}
            sx={{ bgcolor: confirmAction.type === 'delete' ? '#c62828' : '#e65100', borderRadius: 2 }}>
            {actionLoading ? 'Processing...' : confirmAction.type === 'delete' ? 'Delete' : 'Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>
    
      {/* Invite Subcontractors Dialog */}
      <Dialog open={inviteDialog.open} onClose={() => { setInviteDialog({ open: false, scope: null }); setSelectedSubs([]); setInviteAlert(''); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Invite Subcontractors</DialogTitle>
        <DialogContent>
          {inviteAlert && <Alert severity={inviteAlert.includes('success') ? 'success' : 'error'} sx={{ mb: 2 }}>{inviteAlert}</Alert>}
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Select subcontractors to invite for: <strong>{inviteDialog.scope?.title}</strong>
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 300, overflowY: 'auto' }}>
            {subcontractors.map(sub => (
              <Box key={sub.id} onClick={() => { const inviteId = sub.user_id || sub.id; setSelectedSubs(prev => prev.includes(inviteId) ? prev.filter(id => id !== inviteId) : [...prev, inviteId]); }}
                sx={{ p: 1.5, border: '1px solid', borderColor: selectedSubs.includes(sub.user_id || sub.id) ? '#1a237e' : '#e0e0e0',
                  borderRadius: 2, cursor: 'pointer', bgcolor: selectedSubs.includes(sub.user_id || sub.id) ? '#e8eaf6' : 'white',
                  display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#1a237e', fontSize: 14 }}>{(sub.contact_name || sub.first_name || sub.email || '?')[0]?.toUpperCase()}</Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>{sub.contact_name || (sub.first_name ? `${sub.first_name} ${sub.last_name || ''}`.trim() : sub.email)}</Typography>
                  <Typography variant="caption" color="text.secondary">{sub.company_name || sub.email}</Typography>
                </Box>
                {selectedSubs.includes(sub.user_id || sub.id) && <Typography sx={{ color: '#1a237e', fontWeight: 700 }}>✓</Typography>}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => { setInviteDialog({ open: false, scope: null }); setSelectedSubs([]); setInviteAlert(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleInvite} disabled={!selectedSubs.length}
            sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d47a1' } }}>
            Invite {selectedSubs.length > 0 ? `(${selectedSubs.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Scope Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Post New Scope</DialogTitle>
        <DialogContent>
          <TextField fullWidth select label="Project" value={createForm.project_id} 
            onChange={e => setCreateForm({...createForm, project_id: e.target.value})} margin="normal" required>
            {projects.map(p => (
              <MenuItem key={p.id} value={p.id}>{p.address || p.name}</MenuItem>
            ))}
          </TextField>
          <TextField fullWidth label="Title" value={createForm.title} 
            onChange={e => setCreateForm({...createForm, title: e.target.value})} margin="normal" required />
          <TextField fullWidth label="Description" value={createForm.description} 
            onChange={e => setCreateForm({...createForm, description: e.target.value})} margin="normal" multiline rows={3} required />
          <TextField fullWidth select label="Trade Category" value={createForm.trade_category} 
            onChange={e => setCreateForm({...createForm, trade_category: e.target.value})} margin="normal">
            {tradeCategories.map(t => (
              <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>
            ))}
          </TextField>
          <TextField fullWidth label="Budget Estimate ($)" type="number" value={createForm.budget_estimate} 
            onChange={e => setCreateForm({...createForm, budget_estimate: e.target.value})} margin="normal" />
          <TextField fullWidth label="Deadline" type="date" value={createForm.deadline} 
            onChange={e => setCreateForm({...createForm, deadline: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCreateDialog(false)} sx={{ color: '#666' }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateScope} disabled={loading}
            sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d47a1' } }}>
            {loading ? 'Creating...' : 'Create Scope'}
          </Button>
        </DialogActions>
      </Dialog>

</Layout>
  );
}
